import { Link } from 'react-router-dom';
import {
  LanguageIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import Button from './Button';
import { useState, useEffect, useRef } from 'react';
import appointDocLogo from '../assets/appointdoc-logo.png';
import { useTranslation } from '../hooks/useTranslation';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSpecialtiesOpen, setIsSpecialtiesOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('language') || 'EN';
  });
  const specialtiesRef = useRef(null);
  const languageRef = useRef(null);
  const { t } = useTranslation(currentLanguage);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        specialtiesRef.current &&
        !specialtiesRef.current.contains(event.target)
      ) {
        setIsSpecialtiesOpen(false);
      }
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (lang) => {
    localStorage.setItem('language', lang);
    setCurrentLanguage(lang);
    setIsLanguageOpen(false);
    window.location.reload();
  };

  return (
    <header className="w-full py-4 px-4 lg:px-8 border-b fixed top-0 z-50 bg-white">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/">
            <img src={appointDocLogo} alt="AppointDoc" className="w-28 h-12" />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <div className="dropdown dropdown-hover" ref={specialtiesRef}>
            <div
              tabIndex={0}
              role="button"
              onClick={() => setIsSpecialtiesOpen(!isSpecialtiesOpen)}
              className="text-sm hover:text-primary-500 transition-colors cursor-pointer flex items-center gap-1"
            >
              {t('specialties')}
              <ChevronDownIcon
                className={`w-4 h-4 transition-transform ${
                  isSpecialtiesOpen ? 'rotate-180' : ''
                }`}
              />
            </div>
            {isSpecialtiesOpen && (
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow mt-4"
              >
                <li>
                  <Link to="#" className="text-sm">
                    {t('primaryCare')}
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-sm">
                    {t('dentalClinics')}
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-sm">
                    {t('physicalTherapy')}
                  </Link>
                </li>
                <li>
                  <Link to="#" className="text-sm">
                    {t('mentalHealth')}
                  </Link>
                </li>
              </ul>
            )}
          </div>

          <Link
            to="#features"
            className="text-sm hover:text-primary-500 transition-colors"
          >
            {t('features')}
          </Link>
          <Link
            to="#pricing"
            className="text-sm hover:text-primary-500 transition-colors"
          >
            {t('pricing')}
          </Link>
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="dropdown dropdown-hover dropdown-end hidden md:block"
            ref={languageRef}
          >
            <div
              tabIndex={0}
              role="button"
              onClick={() => setIsLanguageOpen(!isLanguageOpen)}
              className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-gray-700 hover:bg-gray-100 cursor-pointer"
            >
              <LanguageIcon className="w-4 h-4" />
              <span>{currentLanguage}</span>
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-40 mt-2"
            >
              <li>
                <button
                  className="text-sm"
                  onClick={() => handleLanguageChange('EN')}
                >
                  English
                </button>
              </li>
              <li>
                <button
                  className="text-sm"
                  onClick={() => handleLanguageChange('ES')}
                >
                  Español
                </button>
              </li>
            </ul>
          </div>
          <Link to="/access">
            <Button variant="ghost" className="hidden md:inline-flex">
              {t('login')}
            </Button>
          </Link>
          <Link to="/access">
            <Button className="hidden md:inline-flex">{t('tryFree')}</Button>
          </Link>

          {/* Mobile Buttons */}
          <div className="md:hidden flex items-center gap-2">
            <Link to="/access">
              <Button>{t('startNow')}</Button>
            </Link>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              {isMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="fixed inset-0 top-[73px] bg-white z-50 md:hidden overflow-y-auto">
          <div className="container mx-auto p-4">
            <nav className="flex flex-col gap-4">
              <div className="border-b pb-4">
                <button
                  onClick={() => setIsSpecialtiesOpen(!isSpecialtiesOpen)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <span className="text-sm">Specialties</span>
                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform ${
                      isSpecialtiesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isSpecialtiesOpen && (
                  <ul className="mt-4 flex flex-col gap-4 pl-4">
                    <li>
                      <Link to="#" className="text-sm block">
                        {t('primaryCare')}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="text-sm block">
                        {t('dentalClinics')}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="text-sm block">
                        {t('physicalTherapy')}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="text-sm block">
                        {t('mentalHealth')}
                      </Link>
                    </li>
                  </ul>
                )}
              </div>

              <Link to="#features" className="text-sm py-4 border-b">
                {t('features')}
              </Link>
              <Link to="#pricing" className="text-sm py-4 border-b">
                {t('pricing')}
              </Link>

              <div className="flex flex-col gap-4 mt-4">
                <div className="dropdown w-full">
                  <div
                    tabIndex={0}
                    role="button"
                    onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                    className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-gray-700 hover:bg-gray-100 cursor-pointer w-full"
                  >
                    <LanguageIcon className="w-4 h-4" />
                    <span>{currentLanguage}</span>
                  </div>
                  <ul
                    tabIndex={0}
                    className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-40 mt-2"
                  >
                    <li>
                      <button
                        className="text-sm"
                        onClick={() => handleLanguageChange('EN')}
                      >
                        English
                      </button>
                    </li>
                    <li>
                      <button
                        className="text-sm"
                        onClick={() => handleLanguageChange('ES')}
                      >
                        Español
                      </button>
                    </li>
                  </ul>
                </div>
                <Link to="/access">
                  <Button variant="ghost" className="w-full">
                    {t('login')}
                  </Button>
                </Link>
                <Link to="/access">
                  <Button className="w-full">{t('tryFree')}</Button>
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
